/**
 * Location verification is server-owned, and the badge is bound to a place.
 *
 * `verifyLocationEurW` writes `isLocationVerified` onto users/{uid} with the
 * Admin SDK, together with `verifiedCity` / `verifiedCountry` — the place the
 * flag was minted for. Firestore rules reject a client write that carries any
 * of the three, and `getPublicUserProfileEurW` returns them as a triple. The
 * web only ever reads them.
 *
 * The bare flag is not enough: it used to survive any later profile edit, so a
 * user could verify where they really were and then change city/country to
 * anywhere and keep the badge. So a badge shows only when the flag is set AND
 * the city/country the profile shows now is the pair the server stamped at
 * verification time (both sides normalised the same way).
 *
 * Grandfather rule: documents verified before `verifiedCity`/`verifiedCountry`
 * existed carry neither, so there is nothing to bind against — they stay
 * verified rather than being mass-unverified, until the user edits their
 * location and the server re-stamps them with a binding.
 *
 * The client-writable `locationValidation.isValid` must never grant a badge.
 *
 * Mirrors src/utils/locationVerification.js in the pplkpr app and
 * functions/services/locationVerification.js in favFuncs; keep the three in
 * step so the app, the web and the server never disagree about one profile.
 */

/** trim + lowercase; anything absent or non-string normalises to ''. */
export function normalizeLocationPart(value: unknown): string {
  if (typeof value !== 'string') return '';
  return value.trim().toLowerCase();
}

export interface LocationVerifiable {
  isLocationVerified?: boolean;
  verifiedCity?: string;
  verifiedCountry?: string;
  city?: string;
  country?: string;
}

/**
 * The one expression every consumer of location trust should use: true only
 * when the server flag is set AND its binding still matches the profile's
 * current city/country (or the doc predates the binding entirely).
 */
export function isLocationVerifiedFor(
  profile: LocationVerifiable | null | undefined,
): boolean {
  if (!profile || profile.isLocationVerified !== true) return false;

  const verifiedCity = normalizeLocationPart(profile.verifiedCity);
  const verifiedCountry = normalizeLocationPart(profile.verifiedCountry);

  // Legacy doc: nothing to bind against.
  if (!verifiedCity && !verifiedCountry) return true;

  if (verifiedCity && verifiedCity !== normalizeLocationPart(profile.city)) return false;
  if (verifiedCountry && verifiedCountry !== normalizeLocationPart(profile.country)) return false;

  return true;
}
